import { useQuery } from '@tanstack/react-query'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'
import axios from '@pepper/utils/src/function/axiosInstance'

import { SuggestedKeyword } from 'types/keyword'

type QueryParams = {
  page: number
  limit: number
  keyword?: string[]
  positionLte?: number
  positionGte?: number
  gteDifficulty?: number
  lteDifficulty?: number
  lteVolume?: number
  gteVolume?: number
  exists?: boolean
  sort?: string
  intent?: 0 | 1 | 2 | 3
}

type Parameters = {
  domainId: string
  companyId: string
  projectId?: string
  themeId?: string
  queryParams?: QueryParams
}

const getExistsParams = (queryParams?: QueryParams) => {
  if (queryParams?.exists) {
    return ['project_id:false', 'self_top_position:false']
  }

  return ['project_id:false']
}

const getComparisonValues = (
  difficulty?: number,
  volume?: number,
  position?: number
) => {
  const comparisonValue = []
  if (difficulty) comparisonValue.push(`difficulty:${difficulty}`)
  if (volume) comparisonValue.push(`volume:${volume}`)
  if (position) comparisonValue.push(`self_top_position:${position}`)
  return comparisonValue
}

const fetchKeywordSuggestions = async ({
  domainId,
  companyId,
  projectId,
  themeId,
  queryParams = {
    page: 1,
    limit: 15,
  },
}: Parameters): Promise<{
  total_records: number
  records: SuggestedKeyword[]
}> => {
  const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/keywords/v2/suggestions`

  const intent = queryParams?.intent

  const lte = getComparisonValues(
    queryParams?.lteDifficulty,
    queryParams?.lteVolume,
    queryParams?.positionLte
  )
  const gte = getComparisonValues(
    queryParams?.gteDifficulty,
    queryParams?.gteVolume,
    queryParams?.positionGte
  )

  const { keyword } = queryParams

  try {
    const response = await axios.get(url, {
      params: {
        project_id: projectId,
        domain_id: domainId,
        theme_id: themeId,
        lte,
        gte,
        intent,
        sort: queryParams?.sort ?? 'volume:desc',
        keyword,
        exists: getExistsParams(queryParams),
      },
      paramsSerializer: {
        indexes: null,
      },
    })

    return response.data
  } catch (error) {
    return error
  }
}

export const useKeywordSuggestions = ({
  projectId,
  domainId,
  companyId,
  themeId,
  enabled,
  queryParams,
}: Parameters & {
  enabled: boolean
}) => {
  return useQuery({
    queryKey: ['keyword-suggestions', themeId, queryParams],
    queryFn: () =>
      fetchKeywordSuggestions({
        projectId,
        domainId,
        companyId,
        themeId,
        queryParams,
      }),
    enabled,
  })
}
